import type { ToastStore } from '@skeletonlabs/skeleton'
import type { QueryClient } from '@tanstack/svelte-query'

import { encodeHTMLEntities } from './encode'
import { FetchError } from './fetch'

export interface CreateMutationHelpersOptions {
  queryClient: QueryClient
  queryKey: unknown[]
  toastStore: ToastStore
}

export const createOnMutate =
  <T, TData>(
    mutator: (runInfo: T, previousData: TData | undefined) => TData,
    options: CreateMutationHelpersOptions,
    afterSetQueryData?: () => void,
  ) =>
  async (runInfo: T) => {
    const { queryClient, queryKey } = options

    // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
    await queryClient.cancelQueries({ queryKey })

    // Snapshot the previous value
    const previousData = queryClient.getQueryData<TData>(queryKey)

    // Optimistically update to the new value
    const newData = mutator(runInfo, previousData)
    queryClient.setQueryData<TData>(queryKey, newData)

    if (afterSetQueryData) {
      afterSetQueryData()
    }

    if (previousData !== undefined) {
      return { previousData }
    }

    return undefined
  }

export const createOnError =
  <TData>(message: string, options: CreateMutationHelpersOptions) =>
  (err: unknown, variables: unknown, context: { previousData: TData } | undefined) => {
    const { queryClient, queryKey, toastStore } = options

    console.error(message, { variables, err })
    if (err instanceof FetchError && err.reason) {
      toastStore.trigger({
        message: encodeHTMLEntities(`${message}: ${err.reason}`),
        background: 'variant-filled-error',
        autohide: false,
      })
    } else {
      toastStore.trigger({
        message: encodeHTMLEntities(message),
        background: 'variant-filled-error',
        autohide: false,
      })
    }
    if (context?.previousData) {
      queryClient.setQueryData<TData>(queryKey, context.previousData)
    }
  }

export const createOnSettled = (options: CreateMutationHelpersOptions) => () => {
  const { queryClient, queryKey } = options
  queryClient.invalidateQueries({ queryKey })
}
